import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import Chip from '@mui/material/Chip';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoReceiptOutline, IoAddOutline, IoTrashOutline, IoCreateOutline, IoSearchOutline } from 'react-icons/io5';

const STATUS_OPTIONS = ['pending_payment', 'confirmed', 'out_for_delivery', 'delivered', 'returned', 'cancelled'];
const PAYMENT_OPTIONS = ['pending', 'paid', 'failed', 'refunded'];

function emptyForm() {
  return {
    event_date: new Date().toISOString().slice(0, 10),
    rent_days: 3,
    status: 'confirmed',
    payment_status: 'paid',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address_line1: '',
    address_line2: '',
    address_city: '',
    address_state: '',
    address_pincode: '',
    coupon_code: '',
    customer_id: '',
    items: [{ productId: '', quantity: 1, rentDays: 3 }],
  };
}

function normalizeLineItemsFromDetail(items) {
  return (items || []).map((it) => ({
    productId: String(it.product_id ?? ''),
    quantity: Number(it.quantity) || 1,
    rentDays: Number(it.rent_days) || 1,
  }));
}

function payloadItemsFromForm(rows) {
  return rows
    .filter((r) => r.productId !== '' && Number(r.productId) > 0)
    .map((r) => ({
      productId: Number(r.productId),
      quantity: Number(r.quantity) || 1,
      rentDays: Number(r.rentDays) || 1,
    }));
}

const STATUS_CHIP_COLORS = {
  pending_payment: 'warning',
  confirmed: 'info',
  out_for_delivery: 'primary',
  delivered: 'success',
  returned: 'default',
  cancelled: 'error',
  refunded: 'default',
};

export default function AdminOrders() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogMode, setDialogMode] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [editFetching, setEditFetching] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const itemsBaselineRef = useRef('');
  const searchTimerRef = useRef(null);

  const load = useCallback((pg = 1, q = '', st = 'all') => {
    setLoading(true);
    const params = new URLSearchParams({ page: pg, per_page: 50 });
    if (q) params.set('search', q);
    if (st && st !== 'all') params.set('status', st);
    api(`/api/admin/orders?${params}`)
      .then((data) => {
        // Support both old array response and new paginated response
        if (Array.isArray(data)) {
          setList(data);
          setTotal(data.length);
          setPages(1);
        } else {
          setList(data.orders || []);
          setTotal(data.total || 0);
          setPages(data.pages || 1);
        }
        setError('');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(1, search, statusFilter); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      load(1, val, statusFilter);
    }, 400);
  };

  const handleStatusFilterChange = (e) => {
    const val = e.target.value;
    setStatusFilter(val);
    setPage(1);
    load(1, search, val);
  };

  const handlePageChange = (_, pg) => {
    setPage(pg);
    load(pg, search, statusFilter);
  };

  useEffect(() => {
    api('/api/admin/products')
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]));
  }, []);

  const handleStatusChange = async (orderId, status) => {
    setError('');
    try {
      await api(`/api/admin/orders/${orderId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      setList((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePaymentStatusChange = async (orderId, payment_status) => {
    setError('');
    try {
      await api(`/api/admin/orders/${orderId}`, { method: 'PATCH', body: JSON.stringify({ payment_status }) });
      setList((prev) => prev.map((o) => (o.id === orderId ? { ...o, payment_status } : o)));
    } catch (err) {
      setError(err.message);
    }
  };

  const openCreate = () => {
    setError('');
    setForm(emptyForm());
    setEditingId(null);
    itemsBaselineRef.current = '';
    setDialogMode('create');
  };

  const openEdit = (orderRow) => {
    setError('');
    setEditingId(orderRow.id);
    setDialogMode('edit');
    setEditFetching(true);
    api(`/api/admin/orders/${orderRow.id}`)
      .then((d) => {
        const linesNorm = normalizeLineItemsFromDetail(d.items);
        itemsBaselineRef.current = JSON.stringify(payloadItemsFromForm(linesNorm));
        setForm({
          event_date: (d.event_date || '').toString().slice(0, 10),
          rent_days: Number(d.rent_days) || 1,
          status: d.status || 'pending_payment',
          payment_status: d.payment_status || 'pending',
          contact_name: d.contact_name || '',
          contact_email: d.contact_email || '',
          contact_phone: d.contact_phone || '',
          address_line1: d.address_line1 || '',
          address_line2: d.address_line2 || '',
          address_city: d.address_city || '',
          address_state: d.address_state || '',
          address_pincode: d.address_pincode || '',
          coupon_code: '',
          customer_id: d.customer_id != null ? String(d.customer_id) : '',
          items:
            linesNorm.length > 0 ? linesNorm : [{ productId: '', quantity: 1, rentDays: 3 }],
        });
      })
      .catch((e) => {
        setError(e.message);
        setDialogMode(null);
      })
      .finally(() => setEditFetching(false));
  };

  const closeDialog = () => {
    if (formSubmitting || editFetching) return;
    setDialogMode(null);
    setEditingId(null);
  };

  const setItemRow = (index, patch) => {
    setForm((f) => {
      const items = [...f.items];
      items[index] = { ...items[index], ...patch };
      return { ...f, items };
    });
  };

  const addItemRow = () => {
    setForm((f) => ({ ...f, items: [...f.items, { productId: '', quantity: 1, rentDays: 3 }] }));
  };

  const removeItemRow = (index) => {
    setForm((f) => ({
      ...f,
      items: f.items.length > 1 ? f.items.filter((_, i) => i !== index) : f.items,
    }));
  };

  const submitCreate = async () => {
    const itemsPayload = payloadItemsFromForm(form.items);
    if (!itemsPayload.length) {
      setError('Add at least one line item with a product.');
      return;
    }
    setError('');
    setFormSubmitting(true);
    try {
      const cid = form.customer_id.trim() === '' ? null : Number(form.customer_id);
      await api('/api/admin/orders', {
        method: 'POST',
        body: JSON.stringify({
          items: itemsPayload,
          event_date: form.event_date,
          status: form.status,
          payment_status: form.payment_status,
          coupon_code: form.coupon_code.trim() || undefined,
          customer_id: cid != null && Number.isFinite(cid) ? cid : undefined,
          contact: {
            name: form.contact_name,
            email: form.contact_email,
            phone: form.contact_phone,
          },
          address: {
            line1: form.address_line1,
            line2: form.address_line2,
            city: form.address_city,
            state: form.address_state,
            pincode: form.address_pincode,
          },
        }),
      });
      closeDialog();
      load(page, search, statusFilter);
    } catch (e) {
      setError(e.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  const submitEdit = async () => {
    if (!editingId) return;
    const itemsPayload = payloadItemsFromForm(form.items);
    if (!itemsPayload.length) {
      setError('Add at least one line item with a product.');
      return;
    }
    setError('');
    setFormSubmitting(true);
    try {
      const cid = form.customer_id.trim() === '' ? null : Number(form.customer_id);
      await api(`/api/admin/orders/${editingId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: form.status,
          payment_status: form.payment_status,
          event_date: form.event_date,
          rent_days: Math.max(1, parseInt(form.rent_days, 10) || 1),
          contact_name: form.contact_name,
          contact_email: form.contact_email,
          contact_phone: form.contact_phone,
          address_line1: form.address_line1,
          address_line2: form.address_line2,
          address_city: form.address_city,
          address_state: form.address_state,
          address_pincode: form.address_pincode,
          customer_id: cid != null && Number.isFinite(cid) ? cid : null,
        }),
      });

      const nowSnap = JSON.stringify(payloadItemsFromForm(form.items));
      if (nowSnap !== itemsBaselineRef.current) {
        await api(`/api/admin/orders/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify({ items: itemsPayload }),
        });
      }

      closeDialog();
      load(page, search, statusFilter);
    } catch (e) {
      setError(e.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setError('');
    setFormSubmitting(true);
    try {
      await api(`/api/admin/orders/${deleteTarget}`, { method: 'DELETE' });
      setDeleteTarget(null);
      load(page, search, statusFilter);
    } catch (e) {
      setError(e.message);
    } finally {
      setFormSubmitting(false);
    }
  };

  const formatPaise = (p) => (p != null ? `₹${(p / 100).toFixed(0)}` : '–');

  const productLabel = (id) => {
    const p = products.find((pr) => String(pr.id) === String(id));
    return p ? `${p.code || ''} · ${p.name || ''} (#${id})` : `#${id}`;
  };

  return (
    <>
      <Card>
        <CardHeader
          title={`Orders${total > 0 ? ` (${total})` : ''}`}
          action={
            <Button variant="contained" startIcon={<IoAddOutline />} onClick={openCreate} sx={{ textTransform: 'none' }}>
              New order
            </Button>
          }
        />
        <CardContent>
          {/* Search + filter row */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by name, email, phone, ID…"
              value={search}
              onChange={handleSearchChange}
              InputProps={{ startAdornment: <IoSearchOutline style={{ marginRight: 8, color: '#888' }} /> }}
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={handleStatusFilterChange}>
                <MenuItem value="all">All statuses</MenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {error && !dialogMode && (
            <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
          )}

          {loading ? (
            <AdminLoading />
          ) : list.length === 0 ? (
            <AdminEmptyState message="No orders found." icon={IoReceiptOutline} />
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Event date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Payment</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map((o) => (
                      <TableRow key={o.id} hover>
                        <TableCell>#{o.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{o.contact_name || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{o.contact_email || ''}</Typography>
                          {o.contact_phone && (
                            <Typography variant="caption" color="text.secondary" display="block">{o.contact_phone}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {o.event_date ? String(o.event_date).slice(0, 10) : '—'}
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={o.status || 'pending_payment'}
                              onChange={(e) => handleStatusChange(o.id, e.target.value)}
                              displayEmpty
                              renderValue={(v) => (
                                <Chip
                                  label={v.replace(/_/g, ' ')}
                                  color={STATUS_CHIP_COLORS[v] || 'default'}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 110 }}>
                            <Select
                              value={o.payment_status || 'pending'}
                              onChange={(e) => handlePaymentStatusChange(o.id, e.target.value)}
                            >
                              {PAYMENT_OPTIONS.map((s) => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>{formatPaise(o.total_paise)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEdit(o)} title="Edit" aria-label="Edit order">
                            <IoCreateOutline size={18} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => { setError(''); setDeleteTarget(o.id); }}
                            title="Delete"
                            aria-label="Delete order"
                            color="error"
                          >
                            <IoTrashOutline size={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {pages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination count={pages} page={page} onChange={handlePageChange} color="primary" size="small" />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!dialogMode} onClose={closeDialog} maxWidth="md" fullWidth disableRestoreFocus>
        <DialogTitle>{dialogMode === 'create' ? 'Create order (manual)' : `Edit order #${editingId}`}</DialogTitle>
        <DialogContent>
          {error && dialogMode && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          {dialogMode === 'edit' && editFetching ? (
            <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
              Loading…
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Event date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={form.event_date}
                  onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
                />
                {dialogMode === 'edit' ? (
                  <TextField
                    label="Order rent days (header)"
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 1 }}
                    value={form.rent_days}
                    onChange={(e) => setForm((f) => ({ ...f, rent_days: e.target.value }))}
                    helperText="Synced to line items on save when dates change via PATCH."
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', px: 1 }}>
                    Use “Rent days” on each line; the backend sets the header from the longest rental.
                  </Typography>
                )}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl size="small" fullWidth>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    Fulfillment status
                  </Typography>
                  <Select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s.replace(/_/g, ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    Payment status
                  </Typography>
                  <Select
                    value={form.payment_status}
                    onChange={(e) => setForm((f) => ({ ...f, payment_status: e.target.value }))}
                  >
                    {PAYMENT_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Typography variant="subtitle2">Contact</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Name"
                  size="small"
                  fullWidth
                  value={form.contact_name}
                  onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                />
                <TextField
                  label="Email"
                  size="small"
                  fullWidth
                  value={form.contact_email}
                  onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                />
                <TextField
                  label="Phone"
                  size="small"
                  fullWidth
                  value={form.contact_phone}
                  onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))}
                />
              </Stack>
              <Typography variant="subtitle2">Address</Typography>
              <TextField
                label="Line 1"
                size="small"
                fullWidth
                value={form.address_line1}
                onChange={(e) => setForm((f) => ({ ...f, address_line1: e.target.value }))}
              />
              <TextField
                label="Line 2"
                size="small"
                fullWidth
                value={form.address_line2}
                onChange={(e) => setForm((f) => ({ ...f, address_line2: e.target.value }))}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="City"
                  size="small"
                  fullWidth
                  value={form.address_city}
                  onChange={(e) => setForm((f) => ({ ...f, address_city: e.target.value }))}
                />
                <TextField
                  label="State"
                  size="small"
                  fullWidth
                  value={form.address_state}
                  onChange={(e) => setForm((f) => ({ ...f, address_state: e.target.value }))}
                />
                <TextField
                  label="Pincode"
                  size="small"
                  fullWidth
                  value={form.address_pincode}
                  onChange={(e) => setForm((f) => ({ ...f, address_pincode: e.target.value }))}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Customer ID (optional)"
                  size="small"
                  fullWidth
                  value={form.customer_id}
                  onChange={(e) => setForm((f) => ({ ...f, customer_id: e.target.value }))}
                  helperText="Must match an existing customer record."
                />
                {dialogMode === 'create' && (
                  <TextField
                    label="Coupon code (optional)"
                    size="small"
                    fullWidth
                    value={form.coupon_code}
                    onChange={(e) => setForm((f) => ({ ...f, coupon_code: e.target.value }))}
                  />
                )}
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Line items</Typography>
                <Button size="small" onClick={addItemRow} sx={{ textTransform: 'none' }}>
                  Add line
                </Button>
              </Box>
              {form.items.map((row, index) => (
                <Stack
                  key={index}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                  sx={{ flexWrap: 'wrap' }}
                >
                  <TextField
                    select
                    label="Product"
                    size="small"
                    sx={{ minWidth: 220, flex: 1 }}
                    value={row.productId}
                    onChange={(e) => setItemRow(index, { productId: e.target.value })}
                  >
                    <MenuItem value="">
                      <em>Select product</em>
                    </MenuItem>
                    {products.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>
                        {productLabel(p.id)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Qty"
                    type="number"
                    size="small"
                    sx={{ width: 88 }}
                    inputProps={{ min: 1 }}
                    value={row.quantity}
                    onChange={(e) => setItemRow(index, { quantity: e.target.value })}
                  />
                  <TextField
                    label="Rent days"
                    type="number"
                    size="small"
                    sx={{ width: 100 }}
                    inputProps={{ min: 1 }}
                    value={row.rentDays}
                    onChange={(e) => setItemRow(index, { rentDays: e.target.value })}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeItemRow(index)}
                    disabled={form.items.length <= 1}
                    aria-label="Remove line"
                  >
                    <IoTrashOutline />
                  </IconButton>
                </Stack>
              ))}
              {dialogMode === 'edit' && (
                <Typography variant="caption" color="text.secondary">
                  Saving updates details first, then replaces line items and recalculates totals if lines changed. Coupon on
                  the order is preserved when recalculating.
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={formSubmitting || editFetching} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={dialogMode === 'create' ? submitCreate : submitEdit}
            disabled={formSubmitting || editFetching}
            sx={{ textTransform: 'none' }}
          >
            {formSubmitting ? 'Saving…' : dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteTarget != null} onClose={() => !formSubmitting && setDeleteTarget(null)}>
        <DialogTitle>Delete order #{deleteTarget}?</DialogTitle>
        <DialogContent>
          {error && (
            <Typography color="error" sx={{ mb: 1 }}>
              {error}
            </Typography>
          )}
          <Typography variant="body2">This removes the order and its line items. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={formSubmitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={formSubmitting} sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
